import Layout from '../../components/Layout';
import React, {Fragment, useEffect, useRef, useState} from 'react';
import PageToolBar, {ToolDivider} from '../../components/PageToolBar';
import {StepStatus, useAppContext} from '../../context/app-context';
import User from '../../model/User';
import DataGrid, {DataGridHandle, FillEvent} from 'react-data-grid';
import ColumnConfig from './columnConfig';
import {
  DeleteDialog,
  FailDialog,
  ResetDialog,
  ResetSuccessDialog,
} from '../../components/Dialog';
import {IconButton} from '../../../../components/Button';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {useMutation, useQuery, useQueryClient} from 'react-query';
import {useAuthContext, useClient} from '../../context/auth-context';
import {v4 as uuidv4} from 'uuid';
import {DateTime} from 'luxon';
import {useCompanyContext} from '../../context/company-context';
import Joyride, { CallBackProps, STATUS, Step, TooltipRenderProps } from 'react-joyride';
import {useXlsExport} from 'hooks/useXlsExport';
import {ReactComponent as DownloadIcon} from 'app/gakki/assets/download.svg';
import {
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  TBodyRow,
} from '@gakki/pages/remain-leave/components/table';
import {toast} from 'react-toastify';
import { JobTitleData } from '../job-title-setting';
export const STEP = 2;

interface GuidedTourState {
  complete: boolean;
  run: boolean;
  steps: Step[];
}

type Option = {
  name: string;
  abbr: string;
};
type Parameters = {
  idType: Option[];
  identityCategory: Option[];
  nationality: Option[];
  maritalStatus: Option[];
};
function parseData(data: string) {
  return data
    .trim()
    .split(/\r\n|\n|\r/)
    .map(row => row.split('\t'));
}
function Tooltip(props: TooltipRenderProps) {
  const {
    backProps,
    closeProps,
    continuous,
    index,
    size,
    isLastStep,
    primaryProps,
    skipProps,
    step,
    tooltipProps,
  } = props;
  const progress = `(${index + 1}/${size})`
  return (
    <div
      {...tooltipProps}
      className="text-[16px] bg-white overflow-hidden w-[300px] h-[calc(170px-16px)] p-[15px] pt-[30px] flex flex-col justify-between rounded-[3px]"
    >
      <div className="text-center">
        {step.title && (
          <h3>
            {step.title}
          </h3>
        )}
        {step.content && <div>{step.content}</div>}
      </div>
      <div className="flex justify-between items-center">
        <div>
          {!isLastStep && (
            <div
              {...skipProps}
              className="text-[13px] text-[#a7a7a7] hover:underline"
              data-test-id="guided-skip"
            >
              略過
            </div>
          )}
        </div>
        <div className="flex">
          {/* {index > 0 && (
            <div {...backProps}>
              返回
            </div>
          )} */}
          {!isLastStep && continuous && 
            <div
              {...primaryProps}
              className="text-[16px] flex items-center h-8 py-1 px-2 text-white bg-brand hover:bg-brand-dark rounded-sm"
              data-test-id="guided-next"
            >
              {`下一步 ${progress}`}
            </div>
          }
          {(isLastStep || !continuous) && 
            <div
              {...closeProps}
              className="text-[16px] flex items-center h-8 py-1 px-2 text-white bg-brand hover:bg-brand-dark rounded-sm"
              data-test-id="guided-complete"
            >
              {`完成 ${progress}`}
            </div>
          }
        </div>
      </div>
    </div>
  );
}

function GuidedContent(props: any) {
  return (
    <span className='font-medium text-[16px]'><span className="text-brand">{`Step ${props.step}. `}</span>{props.children}</span>
  )
}
export default function StepEmployeeData() {
  const api = useClient();
  const queryClient = useQueryClient();
  const {companyId} = useAuthContext();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showFailDialog, setShowFailDialog] = useState(false);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [showResetSuccessDialog, setShowResetSuccessDialog] = useState(false);
  const [selectedRows, setSelectedRows] = useState(() => new Set<React.Key>());
  const {setData: exportExcelData} = useXlsExport();
  const [guidedTourState, setGuidedTourState] = useState<GuidedTourState>({
    complete: false,
    run: false,
    steps: [
      {
        content: <GuidedContent step={1}>下載Excel範本，並填寫資料</GuidedContent>,
        target: '.download-excel-sample-btn',
        disableBeacon: true,
        styles: {
          spotlight: {
            transform: 'scale(1.08)',
          },
        },
      },
      {
        content: <GuidedContent step={2}>點選可新增一筆空白資料</GuidedContent>,
        target: '.add-staff-btn',
        disableBeacon: true,
        styles: {
          spotlight: {
            transform: 'scale(1.08)',
          },
        },
      },
      {
        content: <GuidedContent step={3}>滑鼠點選在新增的「工號前置碼」欄位上，並將Excel資料複製貼上</GuidedContent>,
        target: '.step3-anchor',
        disableBeacon: true,
        placement: 'bottom-start',
      },
      {
        content: <GuidedContent step={4}>點此可重新觀看操作說明</GuidedContent>,
        target: '.guided-tour-btn',
        disableBeacon: true,
        placement: 'right',
        styles: {
          spotlight: {
            transform: 'scale(0.96)',
          },
        },
      },
    ]
  })
  const gridRef = useRef<DataGridHandle>(null);

  const {
    changeStep,
    save,
    edit,
    confirm,
    refetchEnablePY,
    updatedTime,
    stepStatus,
    data,
    init,
    setIsLoading,
    setNoticeDialogProps,
    isLoading,
    saveAndGoStep,
    saveFail,
    onPrev,
  } = useAppContext();
  const {amountOfLicense} = useCompanyContext();

  const {data: parameters} = useQuery<Parameters>('employee-params', () =>
    api.get('staff/params', {
        params: { // 防止cache
          t: new Date(new Date().toISOString().split('T')[0]).getTime()
        }
    }),
  );

  const {data: jobTitleData, status: jobStatus} = useQuery<JobTitleData>(
    'job-title-list',
    () => api.get(`jobtitle/company/${companyId}`).then(d => d),
  );

  function saveData() {
    return api.put(`staff/company/${companyId}`, {
      data: assertionData,
    });
  }

  function confirmData() {
    return api.put(`staff/company/${companyId}/confirm`);
  }

  const {mutate: saveMutate} = useMutation(saveData, {
    onSuccess: () => save(),
    onError: () => saveFail(),
    onMutate: () => setIsLoading(true),
    onSettled: () => setIsLoading(false),
  });

  const {mutate: handleConfirm} = useMutation(
    () => saveData()
    .then(() => refetchEnablePY('next', STEP + 1))
    .then(confirmData),
    {
      onSuccess: (res) => {
        queryClient.invalidateQueries('employee-list');
        confirm();
        changeStep(STEP + 1);
        const resetStaffs = res?.issues?.resetStaffs;
        if (resetStaffs?.length > 0) {
          setNoticeDialogProps({
            isOpen: true,
            text: (
              <div>
                <div className="mb-2.5">以下相關人員資料已異動，已重置相關資料，後續請協助填寫。</div>
                <div className="h-[200px] overflow-auto border border-[#d5dadd] rounded-[3px]">
                  <Table className="!border-none table-fixed !border-collapse !h-auto">
                    <Thead>
                      <Tr>
                        <Th className="sticky top-0">工號</Th>
                        <Th className="sticky top-0">姓名</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {
                        resetStaffs.map((staff: any, idx: number) => {
                          return (
                            <TBodyRow className="max-h-[46px] last:!border-b-[1px]" key={idx}>
                              <Td>{staff.jobNo}</Td>
                              <Td>{`${staff.lastName}${staff.firstName}`}</Td>
                            </TBodyRow>
                          )
                        })
                      }
                    </Tbody>
                  </Table>
                </div>
              </div>
            )
          });
        }
      },
      onError: () => setShowFailDialog(true),
      onMutate: () => setIsLoading(true),
      onSettled: () => setIsLoading(false),
    },
  );

  useEffect(() => {
    setIsLoading(true);
    if (jobStatus === 'success' && jobTitleData) {
      if (data !== null && stepStatus !== StepStatus.init) {
        setIsLoading(false);
        User.validateDuplicate(assertionData);
        return;
      }
      api
        .get(`staff/company/${companyId}`)
        .then(d => {
          const users = d.staffs.map(
            (d: User) =>
              new User({
                ...d,
                jobTitle:
                  d.jobTitle && jobTitleData.jobTitles.indexOf(d.jobTitle) > -1
                    ? d.jobTitle
                    : undefined,
                jobGrade:
                  d.jobGrade && jobTitleData.jobGrades.find(grade => grade.name === d.jobGrade)
                    ? d.jobGrade
                    : undefined,
                jobLevel:
                  d.jobGrade && d.jobLevel && jobTitleData.jobGrades.some(grade => grade.name === d.jobGrade && grade.jobLevels.some(l => l === d.jobLevel))
                    ? d.jobLevel
                    : undefined,
              }),
          );
          User.validateDuplicate(users);
          if (users.length === 0) {
            edit([new User({id: uuidv4(), code: 'DD', seqNo: '001'})]);
            startTour();
          } else {
            init(users);
          }
        })
        .finally(() => setIsLoading(false));
      return;
    }
  }, [jobStatus, jobTitleData]);

  useEffect(() => {
    if (typeof saveAndGoStep === 'number') {
      saveMutate()
    }
  }, [saveAndGoStep])

  // 從excel複製貼上
  function paste(e: ClipboardEvent) {
    e.preventDefault();
    const pastedData = parseData(e.clipboardData?.getData('text/plain') || '');
    
    const newRows = [...assertionData];

    let column = -1;
    let row = -1;

    let elem: Element = e.target as Element;
    while (elem.parentNode) {
      if (elem.getAttribute('aria-colindex')) {
        column = Number(elem.getAttribute('aria-colindex')) - 1; // 扣除掉無用的列 選取框、行數
      }
      if (elem.getAttribute('aria-rowindex')) {
        row = Number(elem.getAttribute('aria-rowindex')) - 2; // 扣除無用的標題行
      }
      if (row !== -1 && column !== -1) {
        break;
      }
      //@ts-ignore
      elem = elem.parentNode;
    }
    
    let isLimit = false;
    pastedData.forEach((d, idx) => {
      if (row + idx >= amountOfLicense) {
        isLimit = true;
        return;
      }
      
      let user: User;
      if (newRows[row + idx]) {
        user = new User(newRows[row + idx]);
      } else {
        user = new User({id: uuidv4()});
      }

      d.forEach((v, idx) => {
        const columnSetting = columnConfig[column + idx];
        if (columnSetting && columnSetting.key) {
          //@ts-ignore
          if (columnSetting.type === 'option') {
            if (columnSetting.options) {
              user[columnSetting.key] = columnSetting.options.find(
                o => o.label === v || o.value === v,
              )?.value;
            }
            if (columnSetting.jobLevelResolver) {
              user[columnSetting.key] = columnSetting.jobLevelResolver(user.jobGrade || '').find(
                o => o.label === v || o.value === v,
              )?.value;
            }
          } else if (
            columnSetting.type === 'numberOption' &&
            columnSetting.options
          ) {
            const value = Number(
              columnSetting.options.find(o => o.label === v || o.value === v)
                ?.value,
            );
            user[columnSetting.key] = !isNaN(value) ? value : undefined;
          } else if (columnSetting.type === 'date') {
            const date = DateTime.fromJSDate(new Date(v));
            if (date.isValid) {
              user[columnSetting.key] = date.toFormat('yyyy/MM/dd');
            }
          } else if (columnSetting.type === 'number') {
            user[columnSetting.key] = !isNaN(Number(v)) ? Number(v) : undefined;
          } else {
            //@ts-ignore
            user[columnSetting.key] = v.trim();
          }
        }
      });

      user.validate();

      newRows[row + idx] = user;
    });
    isLimit && toast.error('已超過授權人數上限，請檢查員工資料', {
      hideProgressBar: true,
      position: 'top-center',
      autoClose: 3000
    });
    User.validateDuplicate(newRows);
    edit(newRows);
  }
  useEffect(() => {
    gridRef.current?.element?.addEventListener('paste', paste);

    return () => gridRef.current?.element?.removeEventListener('paste', paste);
  }, [gridRef.current, paste]);

  const assertionData = data as User[];

  function handleNext() {
    const isError = assertionData.some(
      d => d.errors && Object.values(d.errors).some(e => e !== null),
    );

    if (isError || assertionData.length === 0) {
      setShowFailDialog(true);
      return;
    }

    handleConfirm();
  }

  // 向下複製
  function handleFill({
    columnKey,
    sourceRow,
    targetRows,
  }: FillEvent<User>): User[] {
    const sourceValue = sourceRow[columnKey as keyof User];
    if (columnKey === 'seqNo' && !isNaN(Number(sourceValue))) {
      let increaseNumber = Number(sourceValue);
      return targetRows.map(
        row =>
          new User({
            ...row,
            [columnKey as keyof User]: (++increaseNumber)
              .toString()
              .padStart(sourceValue.length, '0'),
          }),
      );
    }
    if (columnKey === 'jobLevel' && sourceValue) {
      return targetRows.map((row) => {
        const jobGrade = row.jobGrade;
        const isJobLevelExist = jobTitleData?.jobGrades.some((g) => {
          if (g.name === jobGrade) {
            return g.jobLevels.some(l => l === sourceValue);
          };
          return false
        })
        if (isJobLevelExist) {
          return  new User({
            ...row,
            [columnKey as keyof User]: sourceValue,
          })
        }
        return  new User({
          ...row,
          [columnKey as keyof User]: undefined,
        })
      });
    }
    return targetRows.map(
      row =>
        new User({
          ...row,
          [columnKey as keyof User]: sourceValue,
        }),
    );
  }

  function handleDelete() {
    const selected = Array.from(selectedRows.values());
    const newUsers = assertionData.filter(r => !selected.find(s => s === r.id));
    User.validateDuplicate(newUsers);
    edit(newUsers);
    setSelectedRows(new Set<React.Key>());
    setShowDeleteDialog(false);
  }

  // 新增單筆
  function handleInsert() {
    if (data instanceof Array) {
      if (data.length > 0) {
        const latestData: User = data[data.length - 1] as User;
        const newUsers = [
          ...(data as []),
          new User({
            id: uuidv4(),
            code: latestData.code,
            seqNo: latestData.seqNo
              ? (Number(latestData.seqNo) + 1)
                  .toString()
                  .padStart(latestData.seqNo.length, '0')
              : '',
          }),
        ];
        User.validateDuplicate(newUsers);
        edit(newUsers);
      } else if (data.length === 0) {
        edit([new User({id: uuidv4(), code: 'DD', seqNo: '001'})]);
      }
    } else {
      throw new Error('資料類型錯誤');
    }
  }

  function handleRowChange(rows: User[]) {
    const users = rows.map(r => new User(r));
    console.log('users:', users);
    User.validateDuplicate(users);
    edit(users);
  }

  const downloadExcelSample = async () => {
    const downloadUrl = await api.get(`staff/sample/excel`);
    if (downloadUrl) {
      const response = await fetch(downloadUrl, { method: 'get', cache: "no-cache" })
      const blob = await response.blob();
      exportExcelData({blob, name: `員工資料Excel範本.xlsx`});
    }
  }

  const handleJoyrideCallback = (data: CallBackProps) => {
    const { status, index } = data;
    const endState: string[] = [STATUS.FINISHED, STATUS.SKIPPED];
    if (status === STATUS.RUNNING) {
      document.body.style.overflow = 'hidden';
    }
    if (endState.includes(status)) {
      document.body.style.overflow = 'auto';
      setGuidedTourState({ ...guidedTourState, complete: true, run: false });
    } else {
      if (index === 0) {
        // step1 將table置左
        gridRef?.current?.scrollToColumn(2)
      }
      if (index === 2) {
        // step3 將table置頂選到第一列
        (document.querySelectorAll('.__floater__arrow > span')[0] as HTMLElement).style.left = '0';
        gridRef?.current?.scrollToRow(0)
      }
    }
  };

  const startTour = () => setGuidedTourState({ ...guidedTourState, run: true })
  let columnConfig = ColumnConfig(parameters, jobTitleData);
  
  return (
    <Fragment>
      <Layout.MainContent>
        <PageToolBar
          pageTitle={
            <Fragment>
              員工資料
              <span
                className="ml-2 cursor-pointer text-sm text-brand guided-tour-btn hover:underline"
                onClick={startTour}
                data-test-id="start-guided"
              >
                操作說明
              </span>
            </Fragment>
          }
        >
          <IconButton
            onClick={handleInsert}
            disabled={assertionData?.length >= amountOfLicense}
            className={'add-staff-btn text-sm'}
            aria-label="add-employee"
          >
            <FontAwesomeIcon icon={['fal', 'plus']} />
            新增單筆資料
          </IconButton>
          <ToolDivider />
          <IconButton
            onClick={downloadExcelSample}
            className={'download-excel-sample-btn text-sm'}
            aria-label="download-excel-sample"
          >
            <DownloadIcon />
            Excel範本下載
          </IconButton>
          <ToolDivider />
          <IconButton
            onClick={() => setShowResetDialog(true)}
            className={'reset-btn'}
            aria-label="reset-employee"
          >
            <FontAwesomeIcon icon={['fal', 'redo']} />
            全部重設
          </IconButton>
          <ToolDivider />
          <IconButton
            disabled={selectedRows.size === 0}
            onClick={() => setShowDeleteDialog(true)}
            className={'delete-btn'}
            aria-label="delete-employee"
          >
            <FontAwesomeIcon icon={['fal', 'trash-alt']} />
            刪除 {selectedRows.size > 0 ? `(${selectedRows.size})` : ''}
          </IconButton>
        </PageToolBar>
        <div className='relative'>
          <div className='step3-anchor absolute top-[45px] left-[80px] h-[15px] w-[calc(100%-91px)]'/>
          <DataGrid
            style={{
              height: 'calc(100vh - 341px)',
            }}
            className={'rdg-light '}
            columns={columnConfig}
            rows={(data as []) || []}
            defaultColumnOptions={{
              sortable: true,
              resizable: true,
            }}
            onRowsChange={handleRowChange}
            onSelectedRowsChange={setSelectedRows}
            onFill={handleFill}
            selectedRows={selectedRows}
            rowKeyGetter={row => row.id}
            ref={gridRef}
          />
        </div>
      </Layout.MainContent>
      <Layout.Footer
        showNextButton
        showPrevButton
        showSaveButton
        onPrev={onPrev}
        onNext={handleNext}
        onSave={saveMutate}
        updatedTime={updatedTime}
        disabledSaveButton={stepStatus !== StepStatus.edit || isLoading}
        disabledPrevButton={stepStatus === StepStatus.edit || isLoading}
        disabledNextButton={assertionData?.length === 0 || isLoading}
      />
      <DeleteDialog
        isOpen={showDeleteDialog}
        onCancel={() => setShowDeleteDialog(false)}
        onConfirm={handleDelete}
      />
      <ResetDialog
        isOpen={showResetDialog}
        onCancel={() => setShowResetDialog(false)}
        onConfirm={() => {
          edit([]);
          setShowResetDialog(false);
          setShowResetSuccessDialog(true);
          setSelectedRows(new Set<React.Key>());
        }}
      />
      <FailDialog
        isOpen={showFailDialog}
        onConfirm={() => setShowFailDialog(false)}
        onCancel={() => setShowFailDialog(false)}
      />
      <ResetSuccessDialog
        isOpen={showResetSuccessDialog}
        onCancel={() => setShowResetSuccessDialog(false)}
      />
      <Joyride
        scrollToFirstStep
        showProgress
        continuous
        disableScrollParentFix
        disableOverlayClose
        callback={handleJoyrideCallback}
        run={guidedTourState.run}
        steps={guidedTourState.steps}
        tooltipComponent={Tooltip}
        floaterProps={{
          disableAnimation: true,
        }}
        styles={{
          options: {
            overlayColor: 'rgba(53, 61, 69, .4)',
          },
          spotlight: {
            borderRadius: '2px',
          }
        }}
      />
    </Fragment>
  );
}
